import { useEffect, useRef, useState } from 'react';
import styles from './ClosedBetsFilter.module.css';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { SlipStatus } from 'types/enums';
import { CLOSED_BETS_DEFAULT_DAYS } from 'config/constants';
import { getEndOfDay } from 'helpers/dateHelper';

interface Props {
  getSlips: (
    _startDate: Date,
    _endDate: Date,
    _betStatus: SlipStatus,
    _onlyLiveBets?: boolean
  ) => void;
}

export const ClosedBetsFilter = ({ getSlips }: Props) => {
  // Closed bets open on the last 7 days (MyBets loads that range first).
  const [selectedPeriod, setSelectedPeriod] = useState<string>(
    String(CLOSED_BETS_DEFAULT_DAYS)
  );
  const [selectedStatus, setSelectedStatus] = useState<string>('0');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showOnlyLiveBets, setShowOnlyLiveBets] = useState<boolean>(false);
  const { t } = useTranslation();

  const filterClosedBets = async () => {
    let endDateParam = new Date();
    let startDateParam = new Date();
    startDateParam.setHours(0, 0, 0, 0);
    endDateParam = getEndOfDay();
    if (selectedPeriod === 'custom_date') {
      // The list loads once both dates are picked.
      if (!startDate || !endDate) return;
      startDateParam = startDate;
      endDateParam = endDate;
    } else {
      if (selectedPeriod !== '0') {
        startDateParam.setDate(
          startDateParam.getDate() - Number.parseInt(selectedPeriod)
        );
      }
    }

    let status = SlipStatus.CLOSED;
    switch (selectedStatus) {
      case 'winning':
        status = SlipStatus.WINNING;
        break;
      case 'failed':
        status = SlipStatus.FAILED;
        break;
      case 'cancelled':
        status = SlipStatus.CANCELLED;
        break;
      case 'returned':
        status = SlipStatus.RETURNED;
    }

    getSlips(startDateParam, endDateParam, status, showOnlyLiveBets);
  };

  // Every change reloads the list at once - no "Show" button to press. The
  // first render is skipped: MyBets has already loaded the default range.
  const loaded = useRef(false);
  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true;
      return;
    }
    filterClosedBets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedStatus, startDate, endDate, showOnlyLiveBets]);

  return (
    <div className={styles.closedBetsFormWrapper}>
      <div className={styles.formInputValue}>
        <span>{t('select_period_label')}</span>
        <select
          value={selectedPeriod}
          onChange={(event) => {
            setSelectedPeriod(event.target.value);
          }}
        >
          <option value="0">{t('today_label')}</option>
          <option value="3">{t('last_3_days_label')}</option>
          <option value="7">{t('last_7_days_label')}</option>
          <option value="14">{t('last_14_days_label')}</option>
          <option value="30">{t('last_30_days_label')}</option>
          <option value="60">{t('last_60_days_label')}</option>
          <option value="custom_date">{t('another_period_label')}</option>
        </select>
      </div>
      {selectedPeriod === 'custom_date' && (
        <>
          <div className={styles.formInputValue}>
            <span>{t('start_date_label')}</span>
            <DatePicker
              popperPlacement="bottom-start"
              dateFormat={'MM-dd-YYYY'}
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              wrapperClassName={styles.calendar}
              placeholderText={`MM-DD-YYYY (${t('date_format_explanation')})`}
            />
          </div>
          <div className={styles.formInputValue}>
            <span>{t('end_date_label')}</span>
            <DatePicker
              popperPlacement="bottom-start"
              dateFormat={'MM-dd-YYYY'}
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              wrapperClassName={styles.calendar}
              placeholderText={`MM-DD-YYYY (${t('date_format_explanation')})`}
            />
          </div>
        </>
      )}
      <div className={styles.formInputValue}>
        <span>{t('select_bet_by_status_label')}</span>
        <select
          onChange={(event) => {
            setSelectedStatus(event.target.value);
          }}
        >
          <option value="0">{t('show_all_bets_label')}</option>
          <option value="winning">{t('won_bets_label')}</option>
          <option value="failed">{t('lost_bets_label')}</option>
          <option value="cancelled">{t('cancelled_bets_label')}</option>
          <option value="returned">{t('returned_bets_label')}</option>
        </select>
      </div>
      <div className={styles.formInputValue}>
        <div className={styles.checkboxWrapper}>
          <input
            type="checkbox"
            onChange={(event) => setShowOnlyLiveBets(event.target.checked)}
          />
          <span>{t('show_only_live_bets_label')}</span>
        </div>
      </div>
    </div>
  );
};
